import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useNav, type PageId } from "../context/NavContext";
import { Ic } from "./icons";
import { Logo, Card, Button, SectionLabel } from "./ui";
import { useAuth } from "../context/AuthContext";
import { ensureBillingDoc } from "../lib/useConsumeCredit";
import { useEntitlements } from "../lib/useEntitlements";
import { plans as pricingPlans } from "../data/pricing";
import {
  addFicheToFolder,
  subscribeMyFiches,
  type FicheDoc,
} from "../lib/fiches";
import { subscribeMyFolders, type FolderDoc } from "../lib/folders";
import { useRecentFicheIds } from "../lib/recentFiches";
import { SidebarFolderTree } from "./SidebarFolderTree";
import { CreateFolderDialog } from "./CreateFolderDialog";
import { DraggableFicheRow } from "./DragDrop";
import { QuickSwitcher } from "./QuickSwitcher";
import { PastDueBanner } from "./PastDueBanner";
import { openCookieSettings } from "../lib/consent";

const items: { id: PageId; label: string; icon: ReactNode }[] = [
  { id: "dashboard", label: "Dashboard", icon: <Ic.Grid size={16} /> },
  { id: "fiches", label: "Mes fiches", icon: <Ic.File size={16} /> },
  { id: "nouvelle", label: "Nouvelle analyse", icon: <Ic.Plus size={16} /> },
];

/**
 * Format un timestamp d'expiration en suffixe humain ("12 jours", "5 mois").
 * - < 30 j → arrondi en jours
 * - ≥ 30 j → arrondi en mois (jours / 30)
 * Retourne null si déjà expiré ou invalide. Sert au widget sidebar pour
 * afficher l'expiry des crédits topup/pack (vs. l'essai gratuit signup qui
 * a son propre formatage en jours).
 */
function formatExpiryCountdown(expiryMs: number | null): string | null {
  if (expiryMs == null) return null;
  const ms = expiryMs - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days < 30) return `${days} jour${days > 1 ? "s" : ""}`;
  const months = Math.floor(days / 30);
  return `${months} mois`;
}

export const AppShell = ({
  active,
  children,
}: {
  active: PageId;
  children: ReactNode;
}) => {
  const { go, ficheId: activeFicheId } = useNav();
  const { user } = useAuth();
  const { billing, creditsRemaining, creditsSummary } = useEntitlements();

  // Live data for the sidebar organiser. Both subscriptions auto-unsubscribe
  // when the user signs out (the library helpers return early with []).
  const [fiches, setFiches] = useState<FicheDoc[]>([]);
  const [folders, setFolders] = useState<FolderDoc[]>([]);
  useEffect(() => {
    if (!user) {
      setFiches([]);
      setFolders([]);
      return;
    }
    const unsubFiches = subscribeMyFiches(setFiches);
    const unsubFolders = subscribeMyFolders(setFolders);
    return () => {
      unsubFiches();
      unsubFolders();
    };
  }, [user]);

  const recentIds = useRecentFicheIds();

  const [folderDialog, setFolderDialog] = useState<{
    open: boolean;
    editing: FolderDoc | null;
  }>({ open: false, editing: null });

  // Cmd/Ctrl+K opens the quick switcher globally. Only wire the hotkey once
  // the user is signed in — the palette lists fiches/folders that are
  // meaningless otherwise.
  const [switcherOpen, setSwitcherOpen] = useState(false);
  useEffect(() => {
    if (!user) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setSwitcherOpen((v) => !v);
      }
    }
    function onOpenEvent() {
      setSwitcherOpen(true);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("pluview:open-quick-switcher", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pluview:open-quick-switcher", onOpenEvent);
    };
  }, [user]);

  // Drag starts after 5 px of pointer movement so a simple click on a fiche
  // row keeps navigating instead of triggering a drag accidentally.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Track the currently-dragged fiche id so the DragOverlay can render a
  // ghost that follows the pointer. Without it dnd-kit detects drop events
  // but the source element stays in place — visually it looks broken.
  const [draggingFicheId, setDraggingFicheId] = useState<string | null>(null);
  const draggingFiche =
    draggingFicheId != null
      ? fiches.find((f) => f.id === draggingFicheId) ?? null
      : null;

  function handleDragStart(event: DragStartEvent) {
    const id = event.active.data.current?.ficheId as string | undefined;
    setDraggingFicheId(id ?? null);
  }

  async function handleDragEnd(event: DragEndEvent) {
    setDraggingFicheId(null);
    const { active, over } = event;
    if (!over) return;
    const ficheId = active.data.current?.ficheId as string | undefined;
    const folderId = over.data.current?.folderId as string | undefined;
    if (!ficheId || !folderId) return;
    try {
      await addFicheToFolder(ficheId, folderId);
    } catch (err) {
      console.error("addFicheToFolder failed", err);
    }
  }

  const starredFiches = useMemo(
    () => fiches.filter((f) => f.starred && !f.archived).slice(0, 5),
    [fiches],
  );
  // Exclude starred fiches from Récentes — they already show in Épinglées
  // just above, so listing them twice is pure noise.
  const recentFiches = useMemo(() => {
    const byId = new Map(fiches.map((f) => [f.id, f]));
    return recentIds
      .map((id) => byId.get(id))
      .filter((f): f is FicheDoc => !!f && !f.archived && !f.starred)
      .slice(0, 5);
  }, [fiches, recentIds]);

  // Bootstrap le doc /users/{uid}/billing/current dès qu'on a un user connecté.
  // Idempotent côté serveur (no-op si le doc existe déjà), octroie 1 fiche
  // gratuite × 7 jours à la première inscription. Fire-and-forget.
  useEffect(() => {
    if (user) void ensureBillingDoc();
  }, [user]);

  // Widget "Plan" de la sidebar — sémantique post-rapport Cowork batch 2 §6 :
  //   - Le **count** affiché et l'**expiry** affichée se rapportent au MÊME
  //     périmètre (totalRemaining et soonestExpiryMs de creditsSummary).
  //     Évite l'inconsistance "3 fiches restantes · 5 jours" précédente.
  //   - Edge case (downgrade-keep) : `creditsRemaining` peut dépasser le quota
  //     du nouveau plan. Si c'est le cas, on affiche "X fiches dispo (quota
  //     Y/mois)" plutôt qu'un trompeur "0/Y".
  //
  // 4 états distingués :
  //   1) abonné (plan ≠ "free") → plan + quota OU plan + dispo si > quota
  //   2) free + topup/pack       → "Crédits actifs" + dispo + expiry
  //   3) free + essai signup     → "Essai gratuit" + dispo + jours restants
  //   4) free + rien             → "Aucun crédit"
  const planWidget = (() => {
    if (billing && billing.plan !== "free") {
      const plan = pricingPlans.find((pl) => pl.id === billing.plan);
      const quota = plan?.ficheQuota ?? billing.creditsRemaining;
      const planRemaining = billing.creditsRemaining;
      const topup = creditsSummary.topup;

      // Edge case : creditsRemaining > quota → après downgrade-keep, l'user
      // a plus de crédits que ce que son plan actuel prévoit. La barre de
      // progression n'a plus vraiment de sens (toujours 0% utilisé du
      // "quota actuel"). On bascule sur un affichage "X dispo".
      if (planRemaining > quota) {
        const total = planRemaining + topup;
        return {
          label: plan ? `Plan ${plan.name}` : "Abonnement",
          usage: `${total} fiche${total > 1 ? "s" : ""} dispo · quota ${quota}/mois`,
          pct: 0,
          cta: topup > 0 ? "Voir les détails" : "Augmenter le quota",
        };
      }

      const used = Math.max(0, quota - planRemaining);
      const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;

      // Si l'user a aussi des topup credits, on les expose en ligne 2 — sans
      // les mélanger au quota mensuel pour ne pas brouiller la barre.
      const topupSuffix =
        topup > 0
          ? ` · +${topup} add-on${formatExpiryCountdown(creditsSummary.soonestExpiryMs) ? ` (exp ${formatExpiryCountdown(creditsSummary.soonestExpiryMs)})` : ""}`
          : "";

      return {
        label: plan ? `Plan ${plan.name}` : "Abonnement",
        usage: `${used} / ${quota} fiches utilisées${topupSuffix}`,
        pct,
        cta: "Augmenter le quota",
      };
    }
    if (billing && billing.plan === "free") {
      const { topup, free, soonestExpiryMs, totalRemaining } = creditsSummary;

      // Cas (2) : l'user a acheté du topup ou un pack. Count + expiry doivent
      // refléter le même périmètre = totalRemaining (incluant freeCredit s'il
      // est encore là) avec l'expiry soonest correspondante.
      if (topup > 0) {
        const expCopy = formatExpiryCountdown(soonestExpiryMs);
        return {
          label: "Crédits actifs",
          usage: `${totalRemaining} fiche${totalRemaining > 1 ? "s" : ""} dispo${expCopy ? ` · prochaine expiration ${expCopy}` : ""}`,
          pct: 0,
          cta: "Acheter d'autres crédits",
        };
      }
      // Cas (3) : essai gratuit signup, 1 fiche / 7 j.
      if (free > 0 && billing.freeCredit) {
        const days = Math.max(
          0,
          Math.floor(
            (billing.freeCredit.expiresAt.toMillis() - Date.now()) /
              (24 * 60 * 60 * 1000),
          ),
        );
        return {
          label: "Essai gratuit",
          usage: `${free} fiche offerte · ${days} jour${days > 1 ? "s" : ""} restant${days > 1 ? "s" : ""}`,
          pct: 0,
          cta: "Voir les plans",
        };
      }
      // Cas (4) : free sans rien (essai expiré, pas de pack)
      return {
        label: "Aucun crédit",
        usage: "Souscrivez un plan ou achetez un pack",
        pct: 100,
        cta: "Voir les plans",
      };
    }
    return {
      label: "Découvrir Arpentia",
      usage: "1 fiche offerte à l'inscription",
      pct: 0,
      cta: "Voir les plans",
    };
  })();

  const initials = user?.displayName
    ? user.displayName
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() ?? "CP";

  const displayName = user?.displayName ?? user?.email ?? "Camille Perrin";
  const subline = user
    ? user.displayName
      ? user.email ?? "Voir mon profil"
      : "Voir mon profil"
    : "Agence Hexagone · Boulogne";

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDraggingFicheId(null)}
    >
    <div className="min-h-screen bg-canvas flex">
      <aside className="hidden md:flex md:flex-col w-[280px] shrink-0 h-screen sticky top-0 border-r border-ink-200/70 bg-paper/60">
        <div className="px-5 pt-2 pb-3 flex items-center shrink-0">
          <Logo size={28} />
        </div>
        <nav className="px-3 mt-3 shrink-0">
          <ul className="space-y-0.5">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  onClick={() => go(it.id)}
                  className={`w-full flex items-center gap-3 px-3 h-10 rounded-full text-[13.5px] transition
                    ${
                      active === it.id
                        ? "bg-lime-400 text-ink-900 font-medium shadow-[inset_0_-2px_0_rgba(0,0,0,0.06)]"
                        : "text-ink-700 hover:bg-ink-100"
                    }`}
                >
                  <span
                    className={
                      active === it.id ? "text-ink-900" : "text-ink-500"
                    }
                  >
                    {it.icon}
                  </span>
                  {it.label}
                  {it.id === "nouvelle" && active !== "nouvelle" && (
                    <span className="ml-auto text-[10px] font-mono text-ink-400">
                      N
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Organiser — scrollable middle panel. Hidden for anonymous users
            (nothing to show + the subscribe hooks return empty anyway). */}
        {user && (
          <div className="flex-1 min-h-0 overflow-y-auto mt-4 pb-2 px-3 space-y-4">
            {starredFiches.length > 0 && (
              <SidebarFicheList
                label="Épinglées"
                icon={<Ic.Star size={10} />}
                fiches={starredFiches}
                activeFicheId={activeFicheId}
                onOpen={(id) => go("fiche", { ficheId: id })}
              />
            )}
            {recentFiches.length > 0 && (
              <SidebarFicheList
                label="Récentes"
                icon={<Ic.Clock size={10} />}
                fiches={recentFiches}
                activeFicheId={activeFicheId}
                onOpen={(id) => go("fiche", { ficheId: id })}
              />
            )}
            <div>
              <div className="flex items-center justify-between px-1">
                <SectionLabel className="flex items-center gap-1.5">
                  <Ic.Folder size={10} />
                  Dossiers
                </SectionLabel>
                <button
                  onClick={() =>
                    setFolderDialog({ open: true, editing: null })
                  }
                  className="w-5 h-5 rounded-md hover:bg-ink-100 grid place-items-center text-ink-500"
                  title="Nouveau dossier"
                >
                  <Ic.Plus size={12} />
                </button>
              </div>
              <div className="mt-2">
                <SidebarFolderTree
                  folders={folders}
                  fiches={fiches}
                  activeFicheId={activeFicheId}
                  onEditFolder={(folder) =>
                    setFolderDialog({ open: true, editing: folder })
                  }
                />
              </div>
            </div>
          </div>
        )}
        {!user && <div className="flex-1" />}

        <div className="p-3 shrink-0">
          <Card className="!bg-ink-900 !shadow-none text-white p-4 border-0">
            <div className="flex items-center gap-2">
              <Ic.Bolt size={14} className="text-lime-300" />
              <span className="text-[11.5px] text-lime-300 font-mono uppercase tracking-widest">
                {planWidget.label}
              </span>
            </div>
            <div className="mt-2 text-[13.5px]">{planWidget.usage}</div>
            <div className="mt-2 h-1.5 rounded-full bg-ink-700 overflow-hidden">
              <div
                className="h-full bg-lime-400 transition-all"
                style={{ width: `${planWidget.pct}%` }}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              className="mt-3 w-full"
              onClick={() => go("pricing")}
            >
              {planWidget.cta}
            </Button>
          </Card>
          <button
            onClick={() => go("brand")}
            className="mt-2 w-full text-[11.5px] text-ink-500 hover:text-ink-900 transition flex items-center justify-center gap-1.5"
          >
            <Ic.Bolt size={10} className="text-lime-600" />
            Notre histoire · l'arpenteur du XXIᵉ siècle
          </button>
          {/* Liens RGPD/légaux accessibles depuis n'importe quelle page connectée. */}
          <div className="mt-2 flex items-center justify-center gap-2 text-[10.5px] text-ink-400">
            <button
              onClick={() => go("legal")}
              className="hover:text-ink-700 transition"
              title="Mentions légales, CGU, CGV, confidentialité"
            >
              Mentions · CGU · Confidentialité
            </button>
            <span aria-hidden="true">·</span>
            <button
              onClick={openCookieSettings}
              className="hover:text-ink-700 transition"
              title="Gérer mes préférences de cookies"
            >
              Cookies
            </button>
          </div>
        </div>

        <button
          onClick={() => user && go("settings")}
          disabled={!user}
          title={user ? "Ouvrir les paramètres" : "Non connecté"}
          className={`shrink-0 w-full p-3 border-t border-ink-200/70 flex items-center gap-3 text-left transition ${
            user ? "hover:bg-ink-100 cursor-pointer" : "cursor-default"
          } ${active === "settings" ? "bg-ink-100" : ""}`}
        >
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt={displayName}
              referrerPolicy="no-referrer"
              className="w-9 h-9 rounded-full object-cover"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-ink-900 text-lime-300 grid place-items-center text-[12px] font-semibold">
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium truncate">{displayName}</div>
            <div className="text-[11px] text-ink-500 truncate">{subline}</div>
          </div>
          <Ic.ChevronDown size={14} className="text-ink-500" />
        </button>
      </aside>

      <main className="flex-1 min-w-0 pb-16 md:pb-0">
        <PastDueBanner />
        {children}
      </main>

      <CreateFolderDialog
        open={folderDialog.open}
        editing={folderDialog.editing}
        existingFolders={folders}
        onClose={() => setFolderDialog({ open: false, editing: null })}
      />

      <QuickSwitcher
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        fiches={fiches}
        folders={folders}
      />

      <DragOverlay dropAnimation={null}>
        {draggingFiche && (
          <div className="inline-flex items-center gap-2 h-7 px-2.5 rounded-md bg-ink-900 text-white text-[12px] shadow-pop max-w-[240px]">
            <Ic.File size={11} className="text-lime-300 shrink-0" />
            <span className="truncate">{draggingFiche.title}</span>
          </div>
        )}
      </DragOverlay>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-paper/95 backdrop-blur border-t border-ink-200/70 flex items-center justify-around h-16 px-2">
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => go(it.id)}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl text-[10px] transition ${
              active === it.id
                ? "text-ink-900 bg-lime-100 font-medium"
                : "text-ink-500"
            }`}
          >
            <span className={active === it.id ? "text-ink-900" : "text-ink-400"}>
              {it.icon}
            </span>
            {it.label}
          </button>
        ))}
      </nav>
    </div>
    </DndContext>
  );
};

// Compact fiche list used for the "Épinglées" and "Récentes" sidebar
// sections. Renders a labelled group with a title → click rows open the fiche.
function SidebarFicheList({
  label,
  icon,
  fiches,
  activeFicheId,
  onOpen,
}: {
  label: string;
  icon: ReactNode;
  fiches: FicheDoc[];
  activeFicheId: string | null;
  onOpen: (id: string) => void;
}) {
  return (
    <div>
      <SectionLabel className="flex items-center gap-1.5 px-1">
        {icon}
        {label}
      </SectionLabel>
      <ul className="mt-2 space-y-0.5">
        {fiches.map((fiche) => (
          <li key={fiche.id}>
            <DraggableFicheRow ficheId={fiche.id}>
              {({ ref, listeners, attributes, style }) => (
                <button
                  ref={ref as (el: HTMLButtonElement | null) => void}
                  style={style}
                  {...attributes}
                  {...listeners}
                  onClick={() => onOpen(fiche.id)}
                  title={fiche.title}
                  className={`w-full flex items-center gap-2 h-7 px-2 rounded-md text-left text-[12px] truncate transition ${
                    activeFicheId === fiche.id
                      ? "bg-lime-200 text-ink-900"
                      : "text-ink-600 hover:bg-ink-100"
                  }`}
                >
                  <Ic.File size={11} className="text-ink-400 shrink-0" />
                  <span className="truncate">{fiche.title}</span>
                </button>
              )}
            </DraggableFicheRow>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const TopHeader = ({ children }: { children?: ReactNode }) => {
  const { go } = useNav();
  const { user } = useAuth();
  const initials = user?.displayName
    ? user.displayName
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() ?? "CP";
  const displayName = user?.displayName ?? user?.email ?? "Profil";

  return (
    <div className="h-14 sm:h-16 px-4 sm:px-8 flex items-center border-b border-ink-200/70 bg-canvas/80 backdrop-blur sticky top-0 z-20">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          onClick={() =>
            window.dispatchEvent(new CustomEvent("pluview:open-quick-switcher"))
          }
          disabled={!user}
          title={user ? "Recherche rapide (⌘K)" : "Connexion requise"}
          className={`relative hidden sm:flex max-w-[360px] w-full items-center gap-2 bg-paper hairline rounded-full pl-9 pr-3 h-9 text-[13px] text-ink-500 text-left transition ${
            user ? "hover:bg-ink-50 cursor-pointer" : "cursor-not-allowed opacity-60"
          }`}
        >
          <Ic.Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <span className="flex-1 truncate">
            Rechercher une fiche, un dossier…
          </span>
          <kbd className="font-mono text-[10px] text-ink-400 bg-ink-100 px-1.5 py-0.5 rounded">
            ⌘K
          </kbd>
        </button>
      </div>
      <div className="flex items-center gap-2">
        {children}
        <button className="w-9 h-9 rounded-full bg-paper hairline grid place-items-center text-ink-600 relative">
          <Ic.Bell size={15} />
          <span className="absolute top-1.5 right-2 w-1.5 h-1.5 rounded-full bg-brick" />
        </button>
        <button
          onClick={() => user && go("settings")}
          disabled={!user}
          title={user ? "Mes paramètres" : "Non connecté"}
          className={`w-9 h-9 rounded-full overflow-hidden grid place-items-center transition ${
            user ? "hover:ring-2 hover:ring-lime-300 cursor-pointer" : "cursor-default"
          }`}
        >
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt={displayName}
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-ink-900 text-lime-300 grid place-items-center text-[12px] font-semibold">
              {initials}
            </div>
          )}
        </button>
      </div>
    </div>
  );
};
